import { zodResolver } from '@hookform/resolvers/zod'
import {
  Code2,
  Gauge,
  Globe,
  Hash,
  Layers,
  Minus,
  Music,
  Plus,
  Sparkles,
  Wand2,
} from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { FormErrorSummary } from '@/components/feedback/FormErrorSummary'
import { JobProgressPanel } from '@/components/feedback/JobProgressPanel'
import { BackToFactoryLink } from '@/features/factory/components/BackToFactoryLink'
import { GenerationTimeHint } from '@/features/factory/components/GenerationTimeHint'
import { Button } from '@/components/ui/Button'
import { IconButton } from '@/components/ui/IconButton'
import { Segmented, type SegmentedOption } from '@/components/ui/Segmented'
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import {
  useStartCodeLevels,
  type CodeLevelsInput,
} from '@/api/endpoints/factories'
import { useThemes, type Theme } from '@/api/endpoints/themes'
import { useActiveChannelFromUrl } from '@/hooks/useActiveChannel'
import { cn } from '@/lib/cn'
import { LlmProviderPicker } from '../_shared/LlmProviderPicker'
import { PresetBar } from '../_shared/PresetBar'
import { CinemaBackground } from '../ViralShortsForm/CinemaBackground'
import styles from '../ViralShortsForm/ViralShortsForm.module.scss'

const FIELD_LABELS: Record<string, string> = {
  theme: 'Tema',
  count: 'Quantidade',
  language: 'Idioma',
  custom_prompt: 'Sub-tópico',
  difficulty: 'Dificuldade',
  music_mood: 'Música',
}

const LANGUAGES: ReadonlyArray<SegmentedOption<'pt-br' | 'en' | 'both'>> = [
  { value: 'pt-br', label: 'PT-BR' },
  { value: 'en', label: 'EN' },
  { value: 'both', label: 'Ambos' },
]

type DifficultyKey = 'easy' | 'medium' | 'hard'
const DIFFICULTIES: ReadonlyArray<SegmentedOption<DifficultyKey>> = [
  { value: 'easy', label: 'Fácil' },
  { value: 'medium', label: 'Médio' },
  { value: 'hard', label: 'Difícil' },
]

const RANDOM_THEME_OPTION = { value: 'random', label: 'Aleatório (LLM escolhe a linguagem)' } as const

const schema = z.object({
  theme: z.string().min(1, 'Escolha um tema'),
  count: z.number().int().min(1).max(10),
  language: z.enum(['pt-br', 'en', 'both']),
  difficulty: z.enum(['easy', 'medium', 'hard']),
  custom_prompt: z.string().trim().optional(),
  music_mood: z.string().trim().optional(),
  llm_provider: z.enum(['auto', 'cloud', 'local']).optional(),
})

type FormValues = z.infer<typeof schema>

const FORM_DEFAULTS: FormValues = {
  theme: 'code_levels-python_idioms',
  count: 1,
  language: 'pt-br',
  difficulty: 'medium',
  custom_prompt: '',
  music_mood: 'epic',
}

/** Form snapshot → preset payload. Strips ``channel`` / ``count`` (the
 * resolver does the same server-side). */
function formToPayload(values: FormValues): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    theme: values.theme,
    language: values.language,
    difficulty: values.difficulty,
    custom_prompt: values.custom_prompt ?? '',
    music_mood: values.music_mood ?? 'epic',
  }
  return payload
}

function payloadToForm(params: Record<string, unknown>): FormValues {
  return {
    ...FORM_DEFAULTS,
    theme: (params.theme as string | undefined) ?? FORM_DEFAULTS.theme,
    language:
      ((params.language as FormValues['language'] | undefined) ?? null) ?? FORM_DEFAULTS.language,
    difficulty:
      (params.difficulty as FormValues['difficulty'] | undefined) ?? FORM_DEFAULTS.difficulty,
    custom_prompt: (params.custom_prompt as string | undefined) ?? '',
    music_mood: (params.music_mood as string | undefined) ?? FORM_DEFAULTS.music_mood,
  }
}

export function CodeLevelsForm() {
  const { active } = useActiveChannelFromUrl()
  const channel = active?.slug ?? ''

  const { data: allThemes = [], isPending: themesPending } = useThemes()
  // Filter to code_levels-* themes only — the themes endpoint returns
  // every JSON in config/themes/, so we mirror the AtlasForm/QuizForm
  // pattern of slug-prefix filtering client-side.
  const themes = allThemes.filter((t: Theme) => t.slug.startsWith('code_levels-'))

  const startMutation = useStartCodeLevels()

  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const [advancedOpen, setAdvancedOpen] = useState(false)

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      theme: themes[0]?.slug ?? 'code_levels-python_idioms',
      count: 1,
      language: 'pt-br',
      difficulty: 'medium',
      custom_prompt: '',
      music_mood: 'epic',
      llm_provider: 'auto',
    },
  })

  const theme = form.watch('theme')
  const count = form.watch('count')
  const language = form.watch('language')
  const difficulty = form.watch('difficulty')
  const musicMood = form.watch('music_mood')
  const llmProvider = form.watch('llm_provider')

  const onSubmit = form.handleSubmit((values) => {
    const payload: CodeLevelsInput = {
      theme: values.theme,
      channel: channel || null,
      count: values.count,
      language: values.language,
      difficulty: values.difficulty,
      custom_prompt: values.custom_prompt ?? '',
      music_mood: values.music_mood || 'epic',
      force: false,
      llm_provider: values.llm_provider ?? 'auto',
    }
    startMutation.mutate(payload, {
      onSuccess: (data) => {
        setActiveJobId(data.job_id)
      },
    })
  })

  if (activeJobId) {
    return (
      <div className={styles.panelShell}>
        <JobProgressPanel jobId={activeJobId} />
        <div className={styles.panelActions}>
          <Button
            variant="secondary"
            onClick={() => {
              setActiveJobId(null)
              form.reset()
            }}
          >
            Gerar outro
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.shell}>
      <CinemaBackground />
      <div className={styles.content}>
        <header className={styles.header}>
          <div className={styles.kicker}>
            <Code2 aria-hidden /> Code Levels · junior vs senior
          </div>
          <h1 className={styles.title}>Que comparação de código vamos mostrar?</h1>
          <p className={styles.subtitle}>
            Escolha um tema; o Prism gera duas versões do mesmo código (junior verboso vs senior idiomático)
            e digita ambas ao vivo no vídeo. Sem narração — só música.
          </p>
        </header>

        <BackToFactoryLink />

        <form className={styles.glass} onSubmit={onSubmit} noValidate>
          {startMutation.isError ? (
            <FormErrorSummary error={startMutation.error} fieldLabels={FIELD_LABELS} />
          ) : null}
          {channel ? (
            <PresetBar<FormValues>
              factory="code_levels"
              channel={channel}
              formValues={form.watch()}
              formToPayload={formToPayload}
              payloadToForm={payloadToForm}
              onApplyPreset={(values) => {
                form.reset(values)
              }}
            />
          ) : null}

          <div className={styles.rows}>
            <Row icon={<Hash />} label="Tema" htmlFor="code-levels-theme">
              <SelectRoot
                value={theme}
                onValueChange={(v) => {
                  form.setValue('theme', v, { shouldValidate: true })
                }}
              >
                <SelectTrigger id="code-levels-theme" aria-label="Tema">
                  <SelectValue placeholder={themesPending ? 'Carregando...' : 'Escolha um tema'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={RANDOM_THEME_OPTION.value}>{RANDOM_THEME_OPTION.label}</SelectItem>
                  {themes.map((t: Theme) => (
                    <SelectItem key={t.slug} value={t.slug}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </SelectRoot>
              {form.formState.errors.theme ? (
                <span className={styles.errorText}>{form.formState.errors.theme.message}</span>
              ) : null}
            </Row>

            <Row icon={<Gauge />} label="Dificuldade">
              <Segmented<DifficultyKey>
                options={DIFFICULTIES}
                value={difficulty}
                onValueChange={(v) => {
                  form.setValue('difficulty', v)
                }}
                aria-label="Dificuldade"
              />
            </Row>

            <Row icon={<Wand2 />} label="Sub-tópico (opcional)" htmlFor="code-levels-prompt">
              <textarea
                id="code-levels-prompt"
                className={cn(
                  styles.textarea,
                  form.formState.errors.custom_prompt && styles.textareaError,
                )}
                placeholder="Ex: 'algo com async/await' ou 'manipulação de strings'. Vazio = sub-tópico aleatório do tema."
                rows={2}
                {...form.register('custom_prompt')}
              />
            </Row>

            <Row icon={<Layers />} label="Quantidade">
              <div className={styles.stepper}>
                <IconButton
                  aria-label="Diminuir"
                  variant="ghost"
                  size="sm"
                  disabled={count <= 1}
                  onClick={() => {
                    form.setValue('count', Math.max(1, count - 1))
                  }}
                >
                  <Minus />
                </IconButton>
                <span className={styles.stepperValue}>{count}</span>
                <IconButton
                  aria-label="Aumentar"
                  variant="ghost"
                  size="sm"
                  disabled={count >= 10}
                  onClick={() => {
                    form.setValue('count', Math.min(10, count + 1))
                  }}
                >
                  <Plus />
                </IconButton>
              </div>
            </Row>

            {advancedOpen ? (
              <div className={styles.advanced}>
                <AdvSection icon={<Globe />} label="Idioma (legendas + título)">
                  <Segmented<'pt-br' | 'en' | 'both'>
                    options={LANGUAGES}
                    value={language}
                    onValueChange={(v) => {
                      form.setValue('language', v)
                    }}
                    aria-label="Idioma do título e captions"
                  />
                </AdvSection>

                <AdvSection icon={<Music />} label="Música">
                  <input
                    type="text"
                    className={styles.textarea}
                    value={musicMood ?? ''}
                    onChange={(e) => {
                      form.setValue('music_mood', e.target.value)
                    }}
                    placeholder="epic"
                    aria-label="Mood da música"
                  />
                </AdvSection>

                <LlmProviderPicker
                  value={llmProvider ?? 'auto'}
                  onChange={(v) => {
                    form.setValue('llm_provider', v)
                  }}
                />
              </div>
            ) : null}
          </div>

          <footer className={styles.cta}>
            <button
              type="button"
              className={cn(styles.advToggle, advancedOpen && styles.advToggleOpen)}
              onClick={() => {
                setAdvancedOpen((o) => !o)
              }}
            >
              Ajuste fino
            </button>
            <div className={styles.ctaSpacer} />
            <GenerationTimeHint videoKind="code_levels" />
            <button
              type="submit"
              className={styles.generateBtn}
              disabled={!channel || startMutation.isPending}
            >
              <Sparkles aria-hidden />
              {startMutation.isPending ? 'Iniciando...' : 'Gerar'}
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

interface RowProps {
  icon: ReactNode
  label: ReactNode
  htmlFor?: string
  children: ReactNode
}

function Row({ icon, label, htmlFor, children }: RowProps) {
  return (
    <div className={styles.row}>
      {htmlFor ? (
        <label className={styles.rowLabel} htmlFor={htmlFor}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </label>
      ) : (
        <div className={styles.rowLabel}>
          <span className={styles.rowIcon} aria-hidden>
            {icon}
          </span>
          {label}
        </div>
      )}
      <div className={styles.rowControl}>{children}</div>
    </div>
  )
}

interface AdvSectionProps {
  icon: ReactNode
  label: ReactNode
  children: ReactNode
}

function AdvSection({ icon, label, children }: AdvSectionProps) {
  return (
    <section className={styles.advSection}>
      <div className={styles.advSectionLabel}>
        <span className={styles.rowIcon} aria-hidden>
          {icon}
        </span>
        {label}
      </div>
      {children}
    </section>
  )
}
